<template>
  <li class="episode-card" :class="variantClass()">
    <router-link :to="view.startLinkParams(doc)" class="episode-card__link">
      <EpisodeCardCover :doc="doc" />
      <div class="episode-card__text-box">
        <h3 class="episode-card__title">{{ doc.title }}</h3>
        <div class="episode-card__subtitle" v-html="subtitle()"></div>
        <div class="episode-card__actions-box">
          <p class="episode-card__date">
            {{
              doc.original_publish_date
                ? formatDate(doc.original_publish_date)
                : ''
            }}
          </p>

          <div v-if="!IS_PRERENDERING" class="episode-card__controls">
            <DocLibraryMenu
              v-if="view.showActionsButton"
              class="episode-card__actions"
              :docsView="view"
              :doc="doc"
              position="right"
            />
            <FavoriteFlag
              class="books__collection collection collection--book-card episode-card__favorite"
              :doc="doc"
            />
          </div>
        </div>
      </div>
    </router-link>
  </li>
</template>

<script setup lang="ts">
import { EpisodeListItem } from '@/models/interfaces'
import FavoriteFlag from '@/app/components/listing/FavoriteFlag.vue'
import DocLibraryMenu from '@/app/components/doc/DocLibraryMenu.vue'
import EpisodeCardCover from './EpisodeCardCover.vue'
import { formatDate } from '@/helpers/time'
import { PodcastEpisodesView } from '@/models/docs.list.podcasts.episodes'
import { IS_PRERENDERING } from '@/init/settings'

const props = withDefaults(
  defineProps<{
    doc: EpisodeListItem
    mobileVariant?: 'horizontal' | 'vertical'
    view: PodcastEpisodesView
  }>(),
  {
    mobileVariant: 'horizontal',
  },
)

function variantClass(): string {
  return props.mobileVariant === 'horizontal'
    ? 'episode-card--horizontal'
    : 'episode-card--vertical'
}

function subtitle(): string {
  const episode = props.doc
  const podcast = 'doc_group' in episode && episode.doc_group

  if (podcast && props.view.showPodcastAsSubtitle()) {
    return `<p>${podcast.title}</p>`
  }
  return episode.blurb || ''
}
</script>

<style lang="scss">
.episode-card {
  list-style: none;
  // Helps prevent unexpected resizing of items inside flex and grid layouts
  min-width: 0;
  position: relative;

  &__actions-box {
    display: flex;
    justify-content: space-between;
    border-top: 1px solid $c-border-7;
    padding-top: 12px;
    margin-top: 12px;
    align-items: center;
  }

  &__controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__favorite {
    .iconfont {
      line-height: 1;
      width: 24px;
      height: 24px;
    }
  }

  &__date {
    font-size: 14px;
    color: $c-black-3;
    line-height: 16px;
    margin-bottom: 0;
    font-weight: 400;
    padding: 0;
  }

  &__link {
    display: flex;
    flex-direction: column;
    height: 100%;
    background-color: $c-white;
    border-radius: 12px;
    border: 1px solid $c-border-7;

    @include hover {
      text-decoration: inherit;
      color: inherit;
    }

    @include hover {
      border-color: $c-border-2;
      box-shadow: 0px 10px 10px rgb(20 28 49 / 3%);

      .episode-card__img {
        transform: scale(1.07);
      }
    }
  }

  &__img-box {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 12px 12px 0 0;
    background-color: $c-black-5;
    overflow: hidden;
    flex-shrink: 0;

    @media only screen and (max-width: $w-tablet-s) {
      height: fit-content;
      border-radius: 4px;
    }
  }

  &__img {
    height: 202px;
    @media only screen and (max-width: $w-tablet-s) {
      height: 175px;
    }
    max-width: none;
    object-fit: contain;
    transition: transform 0.2s ease-in-out;
  }

  &__text-box {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: 20px;
  }

  &__title {
    display: -webkit-box;
    font-size: 18px;
    line-height: 24px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    color: $c-black-2;
    margin-bottom: 0;
    overflow: hidden;

    @media only screen and (max-width: $w-tablet-s) {
      font-size: 16px;
      line-height: 20px;
    }

    @media only screen and (max-width: $w-tablet-s) {
      -webkit-line-clamp: 2;
    }
  }

  &__subtitle {
    flex: 1;
    p {
      display: -webkit-box;
      margin-top: 4px;
      font-size: 14px;
      line-height: 20px;
      -webkit-line-clamp: 5;
      -webkit-box-orient: vertical;
      text-overflow: ellipsis;
      color: $c-black-3;
      overflow: hidden;
      margin-bottom: 0;
    }
    @media only screen and (max-width: $w-tablet-s) {
      font-size: 12px;
      line-height: 18px;
    }

    @media only screen and (max-width: $w-tablet-s) {
      -webkit-line-clamp: 2;
    }

    @media only screen and (max-width: $w-mobile-s) {
      -webkit-line-clamp: 5;
    }
  }

  // The blurb contains more than one p element, we only show the first one
  &__subtitle p:not(:first-child) {
    display: none;
  }
}

.episode-card--horizontal .episode-card {
  &__link {
    @media only screen and (max-width: $w-tablet-s) {
      gap: 20px;
      flex-direction: row;
      padding: 20px;
      padding-bottom: 12px;
    }
  }
  &__text-box {
    @media only screen and (max-width: $w-tablet-s) {
      padding: 0;
    }
  }
  &__img-box {
    @media only screen and (max-width: $w-tablet-s) {
      align-items: start;
      padding: 0;
      background-color: transparent;
    }
  }
  &__img {
    @media only screen and (max-width: $w-tablet-s) {
      height: auto;
      aspect-ratio: auto;
      width: 88px;
    }
  }

  &__subtitle {
    @media only screen and (max-width: $w-mobile-s) {
      -webkit-line-clamp: 7;
    }
  }
}

.night-mode .episode-card {
  &__link {
    background-color: $c-black-7;
    border-color: $c-dark-3;

    @include hover {
      border-color: $c-dark-8;
      box-shadow: none;
    }
  }

  &__actions-box {
    border-top: 1px solid $c-black-8;
  }

  &__img-box {
    background: $c-dark-2;
  }

  &__title {
    color: $c-dark-4;
  }

  &__subtitle p {
    color: $c-black-6;
  }
}
</style>
